import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import emailjs from '@emailjs/browser';

export default function ContactPage() {
  const form = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.current) return;

    setStatus('sending');

    emailjs.sendForm(
      'service_ezn5t22',        // Service ID
      'template_tomx036',        // Template ID
      form.current,
      'yA_vWtbEz5GKb6fyZ'        // Public Key
    )
    .then((result) => {
      console.log('Success:', result.text);
      setStatus('success');
      alert("✅ Quote request sent successfully! We'll contact you soon.");
      
      if (form.current) form.current.reset();
      setCharCount(0);
    })
    .catch((error) => {
      console.log('Error:', error.text);
      setStatus('error');
      alert('❌ Something went wrong. Please try again or contact us directly.');
    });
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block text-gray-400 text-sm font-semibold tracking-widest uppercase mb-4">Get In Touch</span>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-5 tracking-tight">Contact Us</h1>
          <p className="text-white/65 text-lg max-w-xl mx-auto">
            Ready to transform your home? Get a free, no-obligation quote from Wellington's trusted painting experts.
          </p>
        </div>
      </section>

      <main className="py-16 bg-[#F7F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* Left info */}
            <div className="w-full lg:w-[38%]">
              <h2 className="text-2xl font-black text-gray-900 mb-6 tracking-tight">Get a Free Quote</h2>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Fill in the form and we'll get back to you within 24 hours with a detailed, no-obligation quote. No job too big or too small.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-phone-line', label: 'Phone', value: '022-376-0868', href: 'tel:0223760868' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'alfadecorators0@gmail.com', href: 'mailto:alfadecorators0@gmail.com' },
                  { icon: 'ri-map-pin-line', label: 'Service Area', value: 'Wellington & Hutt Valley, New Zealand', href: null },
                  { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Fri: 7am–6pm | Sat: 8am–4pm', href: null },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#F0F0F0] flex-shrink-0">
                      <i className={`${item.icon} text-[#2D2D2D] text-lg`} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-gray-800 text-sm font-medium hover:text-[#2D2D2D] transition-colors">{item.value}</a>
                      ) : (
                        <div className="text-gray-800 text-sm font-medium">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: '220px' }}>
                <iframe
                  title="ALFA Decorators Service Area - Wellington & Lower Hutt"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24512.1!2d174.9097!3d-41.2090!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d38b1d5e5e5e5e5%3A0x500ef6143a29919!2sLower%20Hutt%2C%20Wellington%205010%2C%20New%20Zealand!5e0!3m2!1sen!2snz!4v1700000000002!5m2!1sen!2snz"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '220px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="mt-6 p-5 bg-[#2D2D2D] rounded-xl text-white">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-time-line text-white/80" />
                  <span className="font-bold text-base">We respond within 24 hours</span>
                </div>
                <p className="text-white/75 text-sm">Our team is ready to help you plan your next project.</p>
              </div>
            </div>

            {/* Form */}
            <div className="w-full lg:w-[62%] bg-white rounded-2xl p-8 md:p-10">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                    <i className="ri-check-line text-green-500 text-3xl" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Quote Request Sent!</h3>
                  <p className="text-gray-500 mb-6">Thank you for reaching out. We'll be in touch within 24 hours with your free quote.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="px-6 py-2.5 bg-[#2D2D2D] text-white rounded-full text-sm font-bold hover:bg-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Request
                  </button>
                </div>
              ) : (
                <form ref={form} onSubmit={sendEmail} id="contact-quote-form">
                  <h3 className="text-xl font-black text-gray-900 mb-6">Request a Free Quote</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <input name="user_name" type="text" required placeholder="John Smith"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2D2D2D] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <input name="user_email" type="email" required placeholder="john@example.com"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2D2D2D] transition-colors" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                      <input name="user_phone" type="tel" placeholder="+64 4 000 0000"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2D2D2D] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Service Required *</label>
                      <div className="relative">
                        <select name="service" required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-[#2D2D2D] transition-colors appearance-none cursor-pointer">
                          <option value="">Select a service...</option>
                          <option value="Interior Painting">Interior Painting</option>
                          <option value="Exterior Painting">Exterior Painting</option>
                          <option value="Roof Painting">Roof Painting</option>
                          <option value="Fine Finish Spraying">Fine Finish Spraying</option>
                          <option value="Plastering & Gib Stopping">Plastering & Gib Stopping</option>
                          <option value="House Washing">House Washing & Water Blasting</option>
                          <option value="Multiple Services">Multiple Services</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Property Address</label>
                    <input name="address" type="text" placeholder="123 Example Street, Wellington"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2D2D2D] transition-colors" />
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Details <span className="text-gray-400 font-normal">({charCount}/500)</span>
                    </label>
                    <textarea name="message" rows={4} maxLength={500}
                      placeholder="Tell us about your project — property size, rooms, preferred timeline, any specific requirements..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2D2D2D] transition-colors resize-none" />
                    {charCount > 500 && <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>}
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm mb-4">Something went wrong. Please try again or call us directly.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full py-4 bg-[#2D2D2D] text-white font-black rounded-full whitespace-nowrap hover:bg-[#1a1a1a] transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-base"
                  >
                    {status === 'sending' ? (
                      <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line" /> Send Quote Request</>
                    )}
                  </button>

                  <p className="text-center text-gray-400 text-xs mt-4">
                    We'll respond within 24 hours. No obligation, completely free.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}